/**
 * The decisions behind "hold, then appear correct": when a cached colour belongs to the session on
 * screen, when the shell may hold its first frame for the read, and what the shell paints.
 *
 * Ported from the mobile app's `brandPaintGate.ts` (`resolveBrandPaint`), with two web facts added.
 *
 * · **A cached answer can belong to the previous session.** Only a deliberate sign-out clears the
 *   query cache; a refused role and a sign-out from another tab do not, and `/me/brand-color`
 *   answers every signed-in account (a client gets its trainer's colour). So a hydration that
 *   starts a NEW session drops the cached read and opens a new generation, and only a read settled
 *   in the current session counts. A hydration inside the app (`rehydrateSession`, a degraded
 *   recovery) keeps its data: same token, same account, and dropping it would flash.
 * · **The hold is once per session.** `_shell`'s `beforeLoad` runs on every in-app navigation; the
 *   first one of a session decides, and every later one passes straight through.
 *
 * Pure: the caller keeps the state and feeds it the events.
 */

import type { ThemeOverrides } from '~/shared/design-system/theme';

import type { BrandColor } from './brandColor';
import type { RememberedBrandPaint } from './brandPaintHint';
import { brandThemeOverrides } from './brandTokens';

export interface BrandPaintSession {
  /** One per hydration, in the order they started. */
  readonly generation: number;
  /** The generation that opened the current session. */
  readonly sessionFrom: number;
  /** Whether a read started in this session has settled, either way. */
  readonly settledThisSession: boolean;
  /** Whether this session's first shell entry has already been decided. */
  readonly held: boolean;
}

export const NO_BRAND_PAINT_SESSION: BrandPaintSession = {
  generation: 0,
  sessionFrom: 0,
  settledThisSession: false,
  held: false,
};

/**
 * A hydration started. `insideTheApp` is whether the session was already in the app (signed in or
 * degraded) when it did. `dropRead` tells the caller to discard the cached answer.
 */
export function brandHydrationStarted(
  state: BrandPaintSession,
  insideTheApp: boolean,
): { readonly state: BrandPaintSession; readonly dropRead: boolean } {
  const generation = state.generation + 1;
  if (insideTheApp) return { state: { ...state, generation }, dropRead: false };
  return {
    state: { generation, sessionFrom: generation, settledThisSession: false, held: false },
    dropRead: true,
  };
}

/** The read started by `generation` settled. A read from before this session changes nothing. */
export function brandReadSettled(state: BrandPaintSession, generation: number): BrandPaintSession {
  if (generation < state.sessionFrom) return state;
  return { ...state, settledThisSession: true };
}

/**
 * The shell is about to be entered. Hold only on the session's FIRST entry, only for an account this
 * browser has never seen, and only while this session's read has not settled.
 */
export function brandShellEntry(
  state: BrandPaintSession,
  accountSeen: boolean,
): { readonly state: BrandPaintSession; readonly hold: boolean } {
  const hold = !state.held && !accountSeen && !state.settledThisSession;
  return { state: { ...state, held: true }, hold };
}

/**
 * What the shell paints. The answer wins the moment there is one, and is `fresh`: the caller writes
 * it back as the next load's hint. Until then the remembered pair stands and nothing is written.
 * `remembered` is only consulted, and only needs reading, while there is no answer.
 */
export function brandPaintFor(
  answer: BrandColor | undefined,
  remembered: RememberedBrandPaint | null,
): { readonly paint: ThemeOverrides | null; readonly fresh: boolean } {
  if (answer !== undefined) return { paint: brandThemeOverrides(answer), fresh: true };
  return { paint: remembered?.kind === 'brand' ? remembered.overrides : null, fresh: false };
}
